import React, { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { api_labelflow_training } from "../../../config.jsx";

import useModelTrainingResult from "./hooks/useModelTrainingResult";
import TrainingResultHeader from "./components/TrainingResultHeader";
import OverviewStrip from "./components/OverviewStrip";
import TrainingChartsSection from "./components/TrainingChartsSection";
import InferencePlayground from "./components/InferencePlayground";
import ExportSection from "./components/ExportSection";
import TrainingMetadataSection from "./components/TrainingMetadataSection";
import useModelPack from "./hooks/useModelPack";

import "./styles/model-training-result.scss";

const STAT_CARDS_CLS = [
  { label: "Top-1 Accuracy", key: "top1_acc", icon: "fa-bullseye", color: "#10b981" },
  { label: "Top-5 Accuracy", key: "top5_acc", icon: "fa-chart-pie", color: "#3b82f6" },
  { label: "Train Loss", key: "train_loss", icon: "fa-chart-line", color: "#6366f1" },
];

const STAT_CARDS_DET = [
  { label: "mAP@50", key: "metrics/mAP50(B)", fallback: "mAP50", icon: "fa-crosshairs", color: "#10b981" },
  { label: "Precision", key: "metrics/precision(B)", fallback: "precision", icon: "fa-bullseye", color: "#3b82f6" },
  { label: "Recall", key: "metrics/recall(B)", fallback: "recall", icon: "fa-search", color: "#f59e0b" },
  { label: "F1 Score", key: "f1", fallback: null, icon: "fa-balance-scale", color: "#6366f1" },
];

const ModelTrainingResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const model = state.model || {};
  const version = state.version || {};
  const modelType = (model.model_type || model.modelType || "").toLowerCase();
  const metrics = version.performanceMetrics || {};
  const trainingParams = version.trainingParams || {};

  const fileInputRef = useRef(null);
  const [inferencePreviewUrl, setInferencePreviewUrl] = useState(null);
  const [isRunningInference, setIsRunningInference] = useState(false);
  const [inferencePredictions, setInferencePredictions] = useState([]);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.5);

  const {
    metricRows,
    metricsLoading,
    exportingOnnx,
    exportingOpenVino,
    setExportingOnnx,
    setExportingOpenVino,
    runInference,
    runInferenceJson,
    exportModel,
  } = useModelTrainingResult(modelType, version._id);

  const {
    pack: deployPack,
    loading: packLoading,
    publishing: packPublishing,
    error: packError,
    publish: publishPack,
    download: downloadPack,
  } = useModelPack(modelType, version._id);

  const statCards = useMemo(
    () => (modelType === "classification" ? STAT_CARDS_CLS : STAT_CARDS_DET),
    [modelType]
  );

  const formatMetric = useCallback(
    (key, fallbackKey) => {
      const raw = metrics[key] ?? (fallbackKey ? metrics[fallbackKey] : undefined);
      if (typeof raw !== "number" || Number.isNaN(raw)) return "-";
      return raw.toFixed(4);
    },
    [metrics]
  );

  const handleBackToModels = useCallback(() => {
    navigate("/ModelManagement", { state: { projectId: state.projectId } });
  }, [navigate, state.projectId]);

  const handleDownloadModel = useCallback(() => {
    navigate("/ModelManagement", {
      state: { projectId: state.projectId, focusModelId: model.model_id, focusVersionId: version._id },
    });
  }, [navigate, state.projectId, model.model_id, version._id]);

  const handleOpenFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInferenceFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file || !modelType || !version._id) return;

      setIsRunningInference(true);
      setInferencePreviewUrl(null);
      setInferencePredictions([]);
      try {
        const [predictions, imageBlob] = await Promise.all([
          runInferenceJson(file, confidenceThreshold),
          runInference(file, confidenceThreshold),
        ]);
        if (Array.isArray(predictions)) setInferencePredictions(predictions);
        if (imageBlob) {
          const objectUrl = URL.createObjectURL(imageBlob);
          setInferencePreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return objectUrl;
          });
        }
      } catch (error) {
        console.error("Inference failed:", error);
        let detail = "Inference failed";
        if (error?.response) {
          try {
            const data = error.response.data;
            detail = typeof data === "string" ? data : data?.detail ?? detail;
          } catch {}
        } else if (error?.message) detail = error.message;
        alert(detail);
      } finally {
        setIsRunningInference(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [modelType, version._id, confidenceThreshold, runInference, runInferenceJson]
  );

  const handleExportOnnx = useCallback(async () => {
    setExportingOnnx(true);
    try {
      const blob = await exportModel("onnx");
      const href = URL.createObjectURL(new Blob([blob], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = href;
      a.download = `${model.model_name || "model"}_${version.version || "v"}_onnx.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (err) {
      console.error("ONNX export failed:", err);
      alert("Failed to export ONNX model. Check training logs or contact administrator.");
    } finally {
      setExportingOnnx(false);
    }
  }, [exportModel, setExportingOnnx, model.model_name, version.version]);

  const handleExportOpenVino = useCallback(async () => {
    setExportingOpenVino(true);
    try {
      const blob = await exportModel("openvino");
      const href = URL.createObjectURL(new Blob([blob], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = href;
      a.download = `${model.model_name || "model"}_${version.version || "v"}_openvino.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (err) {
      console.error("OpenVINO export failed:", err);
      alert("Failed to export OpenVINO model. Check training logs or contact administrator.");
    } finally {
      setExportingOpenVino(false);
    }
  }, [exportModel, setExportingOpenVino, model.model_name, version.version]);

  return (
    <div className="container training-result-page">
      <TrainingResultHeader
        model={model}
        version={version}
        modelType={modelType}
        onBack={handleBackToModels}
        onTestModel={handleOpenFilePicker}
        onDownload={handleDownloadModel}
      />

      <OverviewStrip
        model={model}
        version={version}
        modelType={modelType}
        statCards={statCards}
        formatMetric={formatMetric}
      />

      <TrainingChartsSection
        metricRows={metricRows}
        modelType={modelType}
        metricsLoading={metricsLoading}
      />

      <InferencePlayground
        inferencePreviewUrl={inferencePreviewUrl}
        isRunningInference={isRunningInference}
        inferencePredictions={inferencePredictions}
        confidenceThreshold={confidenceThreshold}
        onConfidenceChange={setConfidenceThreshold}
        onUploadClick={handleOpenFilePicker}
        fileInputRef={fileInputRef}
        onFileChange={handleInferenceFileChange}
      />

      <ExportSection
        modelType={modelType}
        versionId={version._id}
        modelName={model.model_name}
        version={version.version}
        apiBaseUrl={api_labelflow_training}
        exportingOnnx={exportingOnnx}
        exportingOpenVino={exportingOpenVino}
        onExportOnnx={handleExportOnnx}
        onExportOpenVino={handleExportOpenVino}
        onDownloadNative={handleDownloadModel}
        pack={{
          pack: deployPack,
          loading: packLoading,
          publishing: packPublishing,
          error: packError,
          onPublish: publishPack,
          onDownload: downloadPack,
        }}
      />

      <TrainingMetadataSection trainingParams={trainingParams} modelType={modelType} />
    </div>
  );
};

export default ModelTrainingResult;
